import PropTypes from 'prop-types'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Box, Typography, Button, IconButton, Snackbar, Dialog, CircularProgress, Tooltip, Tabs, Tab, TextField, Checkbox, FormControlLabel } from '@mui/material'
import { DeleteOutlineRounded, ZoomOutMapRounded } from '@mui/icons-material'
import { QRCodeSVG } from 'qrcode.react'
import micLogo from '../../../images/Logo.PNG'
import { apiGet, apiPost } from '../Api_Functions'
import SmartDialog from '../utilities/SmartDialog/SmartDialog'
import useSmartTranslation from '../hooks/useSmartTranslation/useSmartTranslation'
import { format as formatDateFns, isValid as isValidDate } from 'date-fns'
import { buildQrPayload } from './qrPayload'
import { PRINTER_FAILURES, printLabel } from './labelPrinting/labelPrintingApi'
import useActiveLabelPrinters from './labelPrinting/useActiveLabelPrinters'
import useLabelPrintFlow from './labelPrinting/useLabelPrintFlow'
import QrPrinterSelect from './labelPrinting/QrPrinterSelect'
import LabelPrintFeedback from './labelPrinting/LabelPrintFeedback'
import QrPrinterPreview from './labelPrinting/QrPrinterPreview'

// Preview QR sizing: it grows to match the printed-text block's height (QR left, text right, same height) so a
// data-heavy label doesn't leave a tiny QR beside a tall column. Floored at QR_MIN for readability, capped at
// QR_MAX for sanity. It does NOT shrink to fit the modal — instead the modal itself grows to the content
// (paperWidth: fit-content, up to 95vw), so the preview never overflows onto the left column.
const QR_MIN = 210
const QR_MAX = 520

// Nested field getter ('location.name'); returns '' when the path does not resolve or the value is null.
export const getFieldValue = (obj, path) => {
  const keys = path.replace(/\[(\w+)\]/g, '.$1').replace(/^\./, '').split('.')
  let current = obj
  for (const key of keys) {
    if (current !== null && current !== undefined && key in current && current[key] !== null) {
      current = current[key]
    } else {
      return ''
    }
  }
  return current === null || current === undefined ? '' : current
}

// Date columns carry these grid `type`s; their raw value (ISO/timestamp) needs formatting.
const DATE_FIELD_TYPES = ['datetime', 'date', 'dateTimePicker', 'datePicker']
const isDateField = (fieldDef) => DATE_FIELD_TYPES.includes(fieldDef.type)
const hasTime = (fieldDef) => fieldDef.type === 'dateTimePicker' || fieldDef.type === 'datetime'
const asDate = (raw) => {
  const d = raw instanceof Date ? raw : new Date(raw)
  return isValidDate(d) ? d : null
}

// Value shown on the printed label / preview: dates in the human, day-first DD/MM/YYYY (language-independent),
// plus HH:mm for date+time. '' when empty.
export const formatFieldValue = (fieldDef, instance) => {
  const raw = getFieldValue(instance, fieldDef.field)
  if (raw === null || raw === undefined || raw === '') return ''
  if (isDateField(fieldDef)) {
    const d = asDate(raw)
    if (d) return formatDateFns(d, hasTime(fieldDef) ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy')
  }
  return raw
}

// Value embedded in the QR string: dates as ISO 8601 (yyyy-MM-dd, +'T'HH:mm for date+time) — compact and
// unambiguous for the person reading the scan (2026-07-22T12:46), unlike the slash-heavy DD/MM/YYYY. Others as-is.
export const formatQrValue = (fieldDef, instance) => {
  const raw = getFieldValue(instance, fieldDef.field)
  if (raw === null || raw === undefined || raw === '') return ''
  if (isDateField(fieldDef)) {
    const d = asDate(raw)
    if (d) return formatDateFns(d, hasTime(fieldDef) ? "yyyy-MM-dd'T'HH:mm" : 'yyyy-MM-dd')
  }
  return raw
}

// RN10 / D6 (S6, S7): visible text = 'Label: value' pairs, one per selected field, in selection order;
// label = the column's i18n label, value formatted (dates included). Empty values omit the whole pair.
// Cosmetic — never part of the QR string.
export const composeVisibleText = (selectedFields, availableFields, instance) =>
  selectedFields
    .map(field => availableFields.find(f => f.field === field))
    .filter(Boolean)
    .map(f => ({ label: f.label, value: formatFieldValue(f, instance) }))
    .filter(({ value }) => value !== null && value !== undefined && value !== '')
    .map(({ label, value }) => `${label}: ${value}`)
    .join('\n')

// Entity-agnostic curation (RN-H2-4): map grid columns to the modal's {field,label,type} shape keeping
// ONLY real scalar fields. Drops columns with no `field` or no `label` (e.g. Tool "D"), any field the
// caller lists in `exclude` (images like `mainImage`, chip/collection join-paths), dedups by `field`
// (Tool reuses `description` across RC/TL/CL/Z), and finally drops any field that resolves to an object
// or array on the instance (e.g. Tool `location`) so no offered field can yield "[object Object]" (H2-S14).
export const buildAvailableFields = (columns, instance, { exclude = [] } = {}) =>
  columns
    .filter(c => c.field && c.name)
    .map(c => ({ field: c.field, label: c.name, type: c.type }))
    .filter(f => !exclude.includes(f.field))
    .filter((f, i, arr) => arr.findIndex(x => x.field === f.field) === i)
    .filter(f => typeof getFieldValue(instance, f.field) !== 'object')

// Historia 226 (RN16b, S129-S131): the field labels sent to Sun with a print, one per LABEL field the screen offers,
// with the label the dialog shows (already in the user's language). A field the screen does not offer goes without
// one: Sun falls back to its English catalogue.
export const fieldLabelsOf = (labelFields, availableFields) => labelFields.reduce((labels, field) => {
  const offered = availableFields.find(f => f.field === field)
  return offered ? { ...labels, [field]: offered.label } : labels
}, {})

const QrCodeGeneratorDialog = ({ open, onClose, entityType, entityLabel, instance, subtitle, availableFields, allowPrinting = true }) => {
  const { t_ } = useSmartTranslation()
  const [versions, setVersions] = useState([]) // [{ id, version, selectedFields, qrFields }]
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [labelFields, setLabelFields] = useState([]) // draft: fields printed on the label (LABEL half of the chip, RN6)
  const [qrFields, setQrFields] = useState([]) // draft: fields embedded in the QR (QR half of the chip); front-built, persisted per version with the template
  const [previewFields, setPreviewFields] = useState([]) // fields shown in the printed-text preview = committed/shown version, NOT the live draft
  const [flashVersion, setFlashVersion] = useState(null) // version card to pulse after a save that reused an existing version (D8)
  const [archivedSnack, setArchivedSnack] = useState(null) // version just archived, kept for the Undo snackbar
  const [zoom, setZoom] = useState(false) // full-size QR overlay for scanning dense codes
  const labelTextRef = useRef(null) // measures the printed-text block so the preview QR can match its height
  const [previewQrSize, setPreviewQrSize] = useState(QR_MIN) // QR grows to the label-text height (never below QR_MIN)
  const [viewTab, setViewTab] = useState(0) // historia 226 (D7): 0 = screen view (as before), 1 = printer view (Sun)

  // CU3 (S24): pin the preview (version highlight + fields/visible text) to a concrete version.
  // No writes — restoring a saved template's label + QR selections is a pure local state change
  // (Option B: the QR is built on the front from the current selection, not stored).
  const commit = (v) => {
    setSelectedVersion(v.version)
    setLabelFields(v.selectedFields)
    setPreviewFields(v.selectedFields) // preview follows the committed version, not later chip edits
    setQrFields(v.qrFields || []) // restore the version's saved QR selection (persisted with the template)
  }

  // CU1 (S23): on open, load the type's saved template versions and preselect the latest.
  useEffect(() => {
    if (!open) return
    const load = async () => {
      const list = await apiGet(`qrTemplates/${entityType}`)
      setVersions(list)
      const latest = list[list.length - 1] // list ordered asc by version (CA10)
      if (latest) {
        commit(latest)
      } else {
        setSelectedVersion(null)
        setLabelFields([])
        setQrFields([])
        setPreviewFields([])
      }
    }
    load()
  }, [open, entityType, instance.id])

  // LABEL half: toggles whether the field is printed on the label. Editing the label selection makes it
  // a draft that no longer matches any saved version, so drop the version highlight (Save reuses/creates, D8).
  const toggleLabel = (field) => {
    setSelectedVersion(null)
    setLabelFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field])
  }

  // QR half: toggles whether the field's value is embedded in the QR string (adapts the QR live).
  // Also drops the version highlight — reuse (D8) needs BOTH selections to match, so editing either half is a draft.
  const toggleQr = (field) => {
    setSelectedVersion(null)
    setQrFields(prev => prev.includes(field) ? prev.filter(f => f !== field) : [...prev, field])
  }

  /**
   * Quitar un campo heredado de la selección. (S381)
   *
   * Solo QUITAR, nunca añadir: ofrecer aquí un campo que esta ventana no sabe resolver sería
   * inventarse una capacidad. Quitarlo, en cambio, es lo único que permite CORREGIR una plantilla
   * que llegó con campos que esta pantalla no puede imprimir.
   *
   * Sale de las dos mitades a la vez porque un campo heredado no tiene mitades que distinguir: no
   * se puede imprimir en la etiqueta ni embeber en el QR, así que separar "quitar del rótulo" de
   * "quitar del QR" ofrecería dos acciones con el mismo efecto visible (ninguno).
   */
  const dropCarriedField = (field) => {
    setSelectedVersion(null)
    setLabelFields(prev => prev.filter(f => f !== field))
    setQrFields(prev => prev.filter(f => f !== field))
  }

  // CU2 (S21/S25): save the selection as a template version. POST; Sun creates a new version or
  // reuses the matching one (D8) and returns its canonical string. This is the only write.
  const saveTemplate = async () => {
    const result = await apiPost('qrTemplates', { entityType, selectedFields: labelFields, qrFields })
    const reused = versions.some(v => v.version === result.version) // D8: the selection matched an existing version
    setVersions(prev => prev.some(v => v.version === result.version) ? prev : [...prev, result]) // append (RN8)
    commit(result)
    if (reused) { // no new version created — pulse its card so the save doesn't look like a no-op
      setFlashVersion(result.version)
      setTimeout(() => setFlashVersion(null), 900)
    }
    return result
  }

  // Historia 226 (D3, CU6): print the label of this element on the chosen printer. MOS opens the dialog with
  // `allowPrinting={false}` (RN46): no selector, no Print, no request for printers.
  const labelPrinters = useActiveLabelPrinters(open && allowPrinting)
  const printFlow = useLabelPrintFlow({
    // The attempt is the printer's newest check (RN31); a printer gone or deactivated meanwhile reloads the list (S140).
    onSettled: (response, error, { printerId }) => {
      const outcome = error?.data?.outcome || ''
      if (response.outcome === 'PRINTED') {
        labelPrinters.updatePrinter(printerId, { availability: 'AVAILABLE', availabilityReason: '', availabilityProblems: [] })
      } else if (PRINTER_FAILURES.includes(outcome)) {
        labelPrinters.updatePrinter(printerId, { availability: 'UNAVAILABLE', availabilityReason: outcome, availabilityProblems: error.data.problems || [] })
      } else if (outcome === 'PRINTER_INACTIVE' || outcome === 'PRINTER_NOT_FOUND') {
        labelPrinters.reload()
      }
    }
  })

  const handlePrinterChange = (printerId) => labelPrinters.choose(printerId)
  const handleMakeDefault = (printerId) => labelPrinters.makeDefault(printerId)
  const handleCopiesChange = (event) => setCopies(event.target.value)
  // E7: copies per label, 1-99 (default 1); outside the range Print is disabled.
  const [copies, setCopies] = useState('1')
  const copiesValid = /^\d{1,2}$/.test(copies) && Number(copies) >= 1
  const [includeTimestamp, setIncludeTimestamp] = useState(false)

  // RN13: the version printed is the one the current selection maps to, so Print saves it first (as "Save template"
  // does) and asks Sun with that version's id. "Print anyway" and Retry repeat without saving again.
  const print = async () => {
    if (!printFlow.hold()) return
    const printer = labelPrinters.chosen
    let version = {}
    try {
      version = await saveTemplate()
    } catch (error) {
      printFlow.release()
      printFlow.fail(error)
      return
    }
    printFlow.release()
    const request = {
      entityType,
      entityId: instance.id,
      printerId: printer.id,
      templateId: version.id,
      copies: Number(copies),
      fieldLabels: fieldLabelsOf(labelFields, availableFields),
      includeTimestamp
    }
    printFlow.attempt((acceptTextTruncation) => printLabel({ ...request, acceptTextTruncation }), printer.name, false, { printerId: printer.id })
  }

  // Archive (soft-delete) a version: hidden from the picker but still resolvable when scanned (labels
  // already issued stay valid). Reversible via the Undo snackbar. If the shown version is the one
  // archived, fall back to the latest remaining.
  const archiveVersion = async (v) => {
    await apiPost(`qrTemplates/version/${v.id}/archive`, { archived: true })
    const remaining = versions.filter(x => x.version !== v.version)
    setVersions(remaining)
    if (selectedVersion === v.version) {
      const latest = remaining[remaining.length - 1]
      if (latest) {
        commit(latest)
      } else {
        setSelectedVersion(null)
        setLabelFields([])
        setQrFields([])
        setPreviewFields([])
      }
    }
    setArchivedSnack(v)
  }

  const undoArchive = async () => {
    const v = archivedSnack
    setArchivedSnack(null)
    if (!v) return
    await apiPost(`qrTemplates/version/${v.id}/archive`, { archived: false })
    setVersions(prev => [...prev, v].sort((a, b) => a.version - b.version))
  }

  // Preview text mirrors the committed/shown version (previewFields), NOT the live checkbox draft —
  // so toggling fields doesn't mutate the preview until the user saves (S6, S7).
  const visibleText = composeVisibleText(previewFields, availableFields, instance)
  const visibleRows = visibleText === '' ? [] : visibleText.split('\n')

  // Match the preview QR's height to the printed-text block: many fields → tall column → bigger QR. The text
  // column has a fixed width, so its height is independent of the QR size — no measure/resize loop. The QR does
  // not shrink to fit the modal; the modal grows to the content instead (see paperWidth below).
  useLayoutEffect(() => {
    const textH = labelTextRef.current?.offsetHeight ?? 0
    setPreviewQrSize(Math.min(QR_MAX, Math.max(QR_MIN, textH)))
  }, [visibleText])

  // Option B: the QR carries the resolvable INSTANCE identity (not the template version) plus the
  // fields marked "QR", and adapts LIVE as those chips toggle. Front-built, version-independent:
  // versions only pick which saved template you load. The whole grammar (keys, escaping, uppercase and
  // alphanumeric-mode rules) lives in qrPayload.js, shared with the reader so both cannot drift.
  // ═══ CAMPOS HEREDADOS: los que la plantilla cargada trae y ESTA ventana no ofrece ═══
  //
  // Las plantillas se guardan POR `entityType`, así que dos pantallas distintas que muestren la
  // misma entidad comparten catálogo. Hoy pasa con FabricationOrder: el listado construye sus
  // campos desde sus ~24 columnas y la ficha de detalle desde una lista propia de cinco, así que
  // una plantilla guardada en una se abre en la otra con campos que la otra no sabe resolver.
  //
  // `commit()` los restaura VERBATIM —y debe seguir haciéndolo: son la plantilla, no un error— pero
  // hasta ahora se colaban en el QR y desaparecían de la etiqueta sin que nadie lo viera.
  const offeredFields = availableFields.map(a => a.field)
  const carriedFields = [...new Set([...labelFields, ...qrFields])].filter(f => !offeredFields.includes(f))

  const qrValueOf = (fieldKey) => formatQrValue(availableFields.find(a => a.field === fieldKey) || { field: fieldKey }, instance)
  // ⚠️ SE FILTRA POR "¿lo ofrece esta ventana?" Y NO POR "¿el valor está vacío?", y la diferencia
  // es todo: S33 fija que un campo OFRECIDO y marcado aquí emite `KEY:` aunque su valor sea vacío,
  // porque el usuario lo eligió en esta pantalla y el hueco es información. Un campo heredado es
  // otra cosa: nadie lo eligió aquí y su valor no es que esté vacío, es que esta ventana no puede
  // resolverlo. Emitirlo daba `WORKFLOW:` en el código impreso, un par que no significa nada y que
  // ocupa sitio en un QR que ya avisa cuando pasa de 220 caracteres.
  const qrEntries = qrFields
    .filter(f => offeredFields.includes(f))
    .map(f => ({ field: f, value: String(qrValueOf(f)) }))
  const qrValue = buildQrPayload({ entityType, id: instance.id, entries: qrEntries })

  // Logo scales INVERSELY with embedded fields: fewer QR fields → sparser code → a bigger logo still scans;
  // more fields → denser code → shrink the logo so it doesn't excavate more modules than H can recover.
  const logoFrac = Math.max(0.12, 0.26 - qrFields.length * 0.02)
  const logoSettings = (size) => ({ src: micLogo, width: Math.round(size * logoFrac), height: Math.round(size * logoFrac / 1.55), excavate: true })

  // Eyebrow comes from the caller (already translated) — the modal no longer derives it from entityType (RN-H2-2).
  const eyebrow = entityLabel
  // Subtitle: the caller-provided identifier (e.g. FabricationOrder's `fabricationOrder`, H2-S5); otherwise
  // fall back to `code · name`; when neither resolves, omit the subtitle line entirely (H2-S6, RN-H2-3).
  const resolvedSubtitle = subtitle != null && subtitle !== ''
    ? subtitle
    : [instance.code, instance.name].filter(Boolean).join(' · ')
  const sectionSx = { fontSize: 14, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#9a9a9a' }

  return (
    <SmartDialog
      setOpen={open}
      close={false}
      accept={false}
      cancel={false}
      title={null}
      closeCallback={onClose}
      paperWidth='fit-content'
      maxPaperWidth='95vw'
      renderComponent={
        <Box sx={{ width: '100%', textAlign: 'left', color: 'text.primary' }}>
          {/* HEADER — eyebrow + title + instance subtitle */}
          <Box sx={{ px: 0.5, pb: 2.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'primary.main', mb: 0.4 }}>{eyebrow}</Typography>
            <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2 }}>{t_('Generate QR code')}</Typography>
            {resolvedSubtitle !== '' && <Typography data-testid='qr-subtitle' sx={{ fontSize: 16, color: '#9a9a9a', mt: 0.4 }}>{resolvedSubtitle}</Typography>}
          </Box>
          {/* E24: the print notice at the top of the dialog, like the rest of MIC */}
          {allowPrinting && <LabelPrintFeedback {...printFlow} />}

          {/* BODY — split: configuration left, preview right */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', border: '1px solid #2f2f2f', borderRadius: 2, overflow: 'hidden' }}>
            {/* LEFT — versions (S17, S23, S25) + field pickers (S17, S19) */}
            <Box sx={{ flex: '1 1 26rem', minWidth: 0, p: 4, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              <Box>
                <Typography sx={sectionSx}>{t_('Template versions')}</Typography>
                {versions.length === 0
                  ? <Typography sx={{ fontSize: 13, color: '#777', mt: 1 }}>{t_('No previous versions')}</Typography>
                  : (
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, mt: 1.5 }}>
                      {versions.map(v => {
                        const sel = v.version === selectedVersion
                        const flash = v.version === flashVersion
                        return (
                          <Box
                            key={v.version}
                            onClick={() => commit(v)}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 2,
                              p: '14px 18px',
                              transition: 'all .15s',
                              border: '1px solid',
                              borderColor: sel ? 'primary.main' : '#3a3a3a',
                              bgcolor: sel ? 'rgba(0,216,160,0.12)' : '#232323',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1.5,
                              '&:hover': { borderColor: sel ? 'primary.main' : '#555', bgcolor: sel ? 'rgba(0,216,160,0.16)' : 'rgba(255,255,255,0.04)' },
                              '&:hover .qr-del': { opacity: 1 }, // reveal the archive (trash) affordance on hover
                              animation: flash ? 'qrFlash 0.45s ease-in-out 2' : 'none', // fumm fumm on reuse (D8)
                              '@keyframes qrFlash': { '0%, 100%': { boxShadow: '0 0 0 0 rgba(79,214,145,0)' }, '50%': { boxShadow: '0 0 0 3px rgba(79,214,145,0.75)' } }
                            }}
                          >
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: sel ? 'primary.main' : 'text.primary' }}>{`${t_('Version')} ${v.version}`}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.08em', color: '#777' }}>{v.selectedFields.length}</Typography>
                              <IconButton
                                className='qr-del'
                                size='small'
                                aria-label={t_('Archive')}
                                onClick={(e) => { e.stopPropagation(); archiveVersion(v) }}
                                sx={{ p: 0.5, opacity: 0, transition: 'opacity .15s', color: '#9a9a9a', '&:hover': { color: '#ff6b6b', bgcolor: 'transparent' } }}
                              >
                                <DeleteOutlineRounded sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                    )}
              </Box>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={sectionSx}>{t_('Available fields')}</Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#777' }}>{t_('Label')} · QR</Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 1, maxHeight: '26rem', overflowY: 'auto' }}>
                  {availableFields.map(f => {
                    const val = getFieldValue(instance, f.field)
                    const labelOn = labelFields.includes(f.field)
                    const qrOn = qrFields.includes(f.field)
                    const half = (on, onClick, ariaLabel, text) => (
                      <Box
                        role='button'
                        aria-label={ariaLabel}
                        onClick={onClick}
                        sx={{
                          px: 1.4,
                          py: 0.6,
                          fontSize: 13,
                          fontWeight: 800,
                          letterSpacing: '0.04em',
                          cursor: 'pointer',
                          userSelect: 'none',
                          bgcolor: on ? 'primary.main' : 'transparent',
                          color: on ? 'primary.contrastText' : '#8a8a8a',
                          '&:hover': { bgcolor: on ? 'primary.main' : 'rgba(255,255,255,0.06)' }
                        }}
                      >
                        {text}
                      </Box>
                    )
                    return (
                      <Box key={f.field} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 0.75, py: 0.7, borderRadius: 1, '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}>
                        <Typography sx={{ flex: 1, fontSize: 16, color: (labelOn || qrOn) ? 'text.primary' : '#9a9a9a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.label}</Typography>
                        {val !== '' && <Typography sx={{ fontSize: 13, color: '#777', maxWidth: '30%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{String(val)}</Typography>}
                        {/* Dual chip: LABEL (printed) and QR (embedded) toggle independently — each half is its own click target */}
                        <Box sx={{ display: 'flex', flexShrink: 0, border: '1px solid #3a3a3a', borderRadius: 1, overflow: 'hidden' }}>
                          {half(labelOn, () => toggleLabel(f.field), `${t_('Label')} ${f.label}`, t_('Label'))}
                          <Box sx={{ width: '1px', bgcolor: '#3a3a3a' }} />
                          {half(qrOn, () => toggleQr(f.field), `QR ${f.label}`, 'QR')}
                        </Box>
                      </Box>
                    )
                  })}
                </Box>

                {/* ═══ (S381) LOS CAMPOS QUE LA PLANTILLA TRAE Y ESTA VENTANA NO OFRECE ═══
                    Antes no se veían: la lista de arriba es `availableFields.map(...)`, así que un
                    campo heredado seguía en la selección —y en lo que se guardaba al pulsar
                    Guardar— sin aparecer por ningún lado. Una plantilla que arrastra campos
                    invisibles es peor que una que los pierde: lo que no se ve no se puede corregir.
                    Solo se pueden QUITAR, nunca marcar: ofrecer aquí un campo que esta ventana no
                    sabe resolver sería inventarse una capacidad que no existe. */}
                {carriedFields.length > 0 && (
                  <Box data-testid='qr-carried-fields' sx={{ mt: 2, p: 1.5, border: '1px dashed #6b5a2a', borderRadius: 1, bgcolor: 'rgba(242,201,76,0.06)' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#f2c94c' }}>
                      {t_('Fields from the saved template that this screen cannot fill')}
                    </Typography>
                    {carriedFields.map(field => (
                      <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        <Typography data-testid={`qr-carried-${field}`} sx={{ flex: 1, fontSize: 14, color: '#c9c9c9', overflowWrap: 'anywhere' }}>{field}</Typography>
                        <IconButton
                          size='small'
                          aria-label={`${t_('Remove')} ${field}`}
                          data-testid={`qr-carried-remove-${field}`}
                          onClick={() => dropCarriedField(field)}
                          sx={{ p: 0.5, color: '#9a9a9a', '&:hover': { color: '#ff6b6b', bgcolor: 'transparent' } }}
                        >
                          <DeleteOutlineRounded sx={{ fontSize: 20 }} />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            {/* RIGHT — preview: QR (front-built, version-independent, live) + front-composed visible text */}
            <Box sx={{ flex: '0 0 auto', bgcolor: '#1d1d1d', borderLeft: '1px solid #2f2f2f', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}>
              {/* Historia 226 (D7, RN44): the screen view below is untouched; Sun's printer view is one more tab (not in MOS) */}
              {allowPrinting && (
                <Tabs value={viewTab} onChange={(event, tab) => setViewTab(tab)} sx={{ alignSelf: 'stretch' }}>
                  <Tab id='qr-view-tab-screen' aria-controls='qr-view-panel-screen' label={t_('Screen view')} />
                  <Tab id='qr-view-tab-printer' aria-controls='qr-view-panel-printer' label={t_('Printer view')} />
                </Tabs>
              )}
              <Box
                id='qr-view-panel-screen'
                role={allowPrinting ? 'tabpanel' : undefined}
                aria-labelledby={allowPrinting ? 'qr-view-tab-screen' : undefined}
                hidden={viewTab !== 0}
                sx={{ display: viewTab !== 0 ? 'none' : 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2.5 }}
              >
                <Box sx={{ bgcolor: '#fff', borderRadius: 2, p: 2.5, boxShadow: '0 3px 12px rgba(0,0,0,0.5)', display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                  <QRCodeSVG
                    value={qrValue}
                    size={previewQrSize}
                    level='Q' // ~25% error correction: covers the small centered MIC logo while keeping the code lighter than H
                    imageSettings={logoSettings(previewQrSize)}
                    style={{ flexShrink: 0 }}
                  />
                  {visibleRows.length > 0 && (
                    <Box ref={labelTextRef} sx={{ display: 'flex', flexDirection: 'column', gap: 0.7, width: '14rem', flexShrink: 0 }}>
                      {visibleRows.map((line, i) => (
                        <Typography key={i} sx={{ fontSize: 16, fontWeight: 600, color: '#111', lineHeight: 1.3, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{line}</Typography>
                      ))}
                    </Box>
                  )}
                </Box>
                {qrFields.length > 0 && (
                  <Typography sx={{ fontSize: 14, color: qrValue.length > 220 ? '#f2c94c' : '#8a8a8a' }}>
                    {t_('QR payload')}: {qrValue.length}{qrValue.length > 220 ? ' · ≥ 20 mm' : ''}
                  </Typography>
                )}
                <Button size='small' startIcon={<ZoomOutMapRounded />} onClick={() => setZoom(true)} sx={{ color: '#bbb', fontSize: 13 }}>{t_('View')}</Button>
              </Box>
              {allowPrinting && viewTab === 1 && (
                <Box id='qr-view-panel-printer' role='tabpanel' aria-labelledby='qr-view-tab-printer' sx={{ width: 'min(30rem, 70vw)' }}>
                  <QrPrinterPreview
                    active
                    request={{
                      entityType,
                      entityId: instance.id,
                      printerId: labelPrinters.printerId,
                      selectedFields: labelFields,
                      qrFields,
                      fieldLabels: fieldLabelsOf(labelFields, availableFields),
                      includeTimestamp
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>

          {/* FOOTER — printer selector (historia 226) + Cancel + Save template + Print */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1.5, pt: 3, flexWrap: 'wrap' }}>
            {allowPrinting && (
              <QrPrinterSelect printers={labelPrinters.printers} printerId={labelPrinters.printerId} defaultId={labelPrinters.defaultId} onChange={handlePrinterChange} onMakeDefault={handleMakeDefault} />
            )}
            {allowPrinting && (
              <TextField
                label={t_('Copies')}
                size='small'
                value={copies}
                onChange={handleCopiesChange}
                error={!copiesValid}
                helperText={copiesValid ? '' : t_('Between 1 and 99')}
                inputProps={{ inputMode: 'numeric', maxLength: 3 }}
                sx={{ width: 110 }}
              />
            )}
            {allowPrinting && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeTimestamp}
                    onChange={(event) => setIncludeTimestamp(event.target.checked)}
                    size='small'
                  />
                }
                label={t_('Include date and time')}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <Button size='large' onClick={onClose} sx={{ color: '#ccc', fontSize: 15 }}>{t_('Cancel')}</Button>
            <Button size='large' variant={allowPrinting ? 'outlined' : 'contained'} onClick={saveTemplate} sx={{ fontSize: 15 }}>{t_('Save template')}</Button>
            {allowPrinting && (
              <Tooltip title={labelPrinters.printers.length === 0 ? t_('No printers configured. Add one in Configuration → Printers.') : ''}>
                <span>
                  <Button
                    size='large'
                    variant='contained'
                    onClick={print}
                    disabled={labelPrinters.printerId === '' || printFlow.busy || !copiesValid}
                    startIcon={printFlow.busy ? <CircularProgress size={18} color='inherit' /> : undefined}
                    sx={{ fontSize: 15 }}
                  >
                    {printFlow.busy ? t_('Printing…') : t_('Print')}
                  </Button>
                </span>
              </Tooltip>
            )}
          </Box>

          {/* Undo safety net for the (reversible) archive — no confirm dialog needed */}
          <Snackbar
            open={Boolean(archivedSnack)}
            autoHideDuration={5000}
            onClose={() => setArchivedSnack(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            message={archivedSnack ? `${t_('Version archived')}: ${t_('Version')} ${archivedSnack.version}` : ''}
            action={<Button color='primary' size='small' onClick={undoArchive}>{t_('Undo')}</Button>}
          />

          {/* Full-size QR overlay — for scanning dense codes (many embedded fields) */}
          <Dialog open={zoom} onClose={() => setZoom(false)} maxWidth={false}>
            <Box sx={{ bgcolor: '#fff', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <QRCodeSVG value={qrValue} size={440} level='Q' imageSettings={logoSettings(440)} />
              <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: '#555', wordBreak: 'break-all', maxWidth: 440, textAlign: 'center' }}>{qrValue}</Typography>
            </Box>
          </Dialog>
        </Box>
      }
    />
  )
}

QrCodeGeneratorDialog.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  entityType: PropTypes.oneOf(['RawMaterial', 'Part', 'Fixture', 'Tool', 'Order', 'FabricationOrder']).isRequired,
  entityLabel: PropTypes.string,
  subtitle: PropTypes.string,
  instance: PropTypes.object.isRequired,
  availableFields: PropTypes.arrayOf(
    PropTypes.shape({ field: PropTypes.string, label: PropTypes.string, type: PropTypes.string })
  ).isRequired,
  allowPrinting: PropTypes.bool
}

export default QrCodeGeneratorDialog
